import React, { useEffect, useRef, useState } from "react";
import { api, type Me } from "./api";
import { useProblems, FieldProblem, invalid, type Rule, type Problems } from "./form-check";
import { appNow } from "./clock";
import { easternMinutes, easternToUtcMs } from "../server/station/windows";
import { stamp, DISPOSITION_LABEL } from "./Pass";
import { PersonBanner, KIND_LABEL, type PersonHeader } from "./Otc";
import { VisitEscalation } from "./VisitEscalation";

/**
 * Visits and incidents (build plan §10.1 #3; Baseline §5.3): one record for
 * injury, illness or other, students and staff. Time in/out, complaint,
 * optional vitals, assessment, treatment, disposition, follow-up; for an
 * injury, the claim form's own fields (Part I 10–14). Phone-first (375 px).
 * A save fills what is blank; changing something already recorded is a new
 * version with a reason, the original kept in the history.
 */

export interface Vitals {
  temp: string;
  pulse: string;
  bp: string;
  resp: string;
  spo2: string;
  takenAt: string;
}
export interface ReferralStep {
  step: string;
  atUtc: string;
  contact: string;
  outcome: string;
}
export interface VisitView {
  visitKey: string;
  personKey: string;
  personType: string;
  personName: string;
  personDateOfBirth: string;
  kind: string;
  startedAtUtc: string;
  endedAtUtc: string;
  occurredAtUtc: string;
  location: string;
  offCampus: boolean;
  activity: string;
  narrative: string;
  bodyParts: string[];
  natureOfInjury: string;
  complaint: string;
  vitals: Vitals | null;
  assessment: string;
  treatment: string;
  disposition: string;
  referralSteps: ReferralStep[];
  transportedTo: string;
  accompaniedBy: string;
  returnedAtUtc: string;
  supervisingAdult: string;
  supervisingTitle: string;
  supervisorWasWitness: string;
  witnesses: string;
  claim: Record<string, string>;
  outsideCare: string;
  outsideCareProvider: string;
  insuranceNoticeStatus: string;
  insuranceNoticeDueOn: string;
  directorNotifiedUtc: string;
  followUpDueOn: string;
  followUpStatus: string;
  status: string;
  recordedByEmail: string;
  recordedByName: string;
  countersignRequired: boolean;
  countersignedByEmail: string;
  countersignedAtUtc: string;
  correctsVisitKey: string;
  correctionReason: string;
  notes: string;
}
export interface VisitOptions {
  kinds: string[];
  dispositions: { code: string; label: string }[];
  bodyParts: string[];
  natures: string[];
  claimQuestions: { key: string; label: string }[];
  narrativeExample: string;
  canEdit: boolean;
  licensed: boolean;
}
export interface VisitDetail {
  person: PersonHeader;
  visit: VisitView;
  latestVisitKey: string;
  history: VisitView[];
  given: { adminKey: string; kind: string; indication: string; disposition: string; amountGiven: string; givenAtUtc: string; administeredByName: string; administeredByEmail: string }[];
  notifications: { notificationKey: string; tier: string; method: string; status: string; toName: string; subject: string; createdUtc: string }[];
  options: VisitOptions;
  canCountersign: boolean;
  escalation: { step: string; label: string; contact: string; set: boolean }[];
  directorRequired: boolean;
  canErPacket: boolean;
}

export const STEP_LABEL: Record<string, string> = { CampusResponders: "Campus first responders", Call911: "911 (then campus security)", LocalEms: "Local EMS", RemoteProvider: "Remote provider", UrgentCare: "Urgent care", ER: "Emergency room" };

/** "HH:MM" in New York for an instant (the form's times). */
export function hhmmOf(iso: string): string {
  if (!iso) return "";
  const m = easternMinutes(Date.parse(iso));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
const nowHHMM = () => hhmmOf(new Date(appNow()).toISOString());

interface Form {
  kind: string;
  startedAt: string;
  complaint: string;
  location: string;
  offCampus: boolean;
  activity: string;
  occurredAt: string;
  narrative: string;
  bodyParts: string[];
  natureOfInjury: string;
  vitals: Vitals;
  assessment: string;
  treatment: string;
  disposition: string;
  supervisingAdult: string;
  supervisingTitle: string;
  supervisorWasWitness: string;
  witnesses: string;
  claim: Record<string, string>;
  outsideCare: string;
  outsideCareProvider: string;
  followUpDueOn: string;
  followUpStatus: string;
  endedAt: string;
  notes: string;
  correctionReason: string;
}

const formOf = (v: VisitView | null, complaint = ""): Form => ({
  kind: v?.kind ?? "Illness",
  startedAt: v ? hhmmOf(v.startedAtUtc) : nowHHMM(),
  complaint: v?.complaint ?? complaint,
  location: v?.location ?? "",
  offCampus: v?.offCampus ?? false,
  activity: v?.activity ?? "",
  occurredAt: v ? hhmmOf(v.occurredAtUtc) : "",
  narrative: v?.narrative ?? "",
  bodyParts: v?.bodyParts ?? [],
  natureOfInjury: v?.natureOfInjury ?? "",
  vitals: v?.vitals ?? { temp: "", pulse: "", bp: "", resp: "", spo2: "", takenAt: "" },
  assessment: v?.assessment ?? "",
  treatment: v?.treatment ?? "",
  disposition: v?.disposition ?? "",
  supervisingAdult: v?.supervisingAdult ?? "",
  supervisingTitle: v?.supervisingTitle ?? "",
  supervisorWasWitness: v?.supervisorWasWitness ?? "",
  witnesses: v?.witnesses ?? "",
  claim: v?.claim ?? {},
  outsideCare: v?.outsideCare ?? "",
  outsideCareProvider: v?.outsideCareProvider ?? "",
  followUpDueOn: v?.followUpDueOn ?? "",
  followUpStatus: v?.followUpStatus ?? "",
  endedAt: v ? hhmmOf(v.endedAtUtc) : "",
  notes: v?.notes ?? "",
  correctionReason: "",
});

/** The fields that, once recorded, only change as a new version (with a reason). */
const WRITE_ONCE: (keyof Form)[] = ["kind", "startedAt", "complaint", "location", "activity", "occurredAt", "narrative", "natureOfInjury", "assessment", "treatment", "disposition", "supervisingAdult", "supervisingTitle", "supervisorWasWitness", "witnesses", "endedAt", "notes"];
export function rewritten(orig: Form, f: Form): string[] {
  const out: string[] = WRITE_ONCE.filter((k) => String(orig[k] ?? "") !== "" && String(orig[k]) !== String(f[k]));
  if (orig.bodyParts.length && orig.bodyParts.join(",") !== f.bodyParts.join(",")) out.push("bodyParts");
  if (JSON.stringify(orig.vitals) !== JSON.stringify(f.vitals) && Object.values(orig.vitals).some(Boolean)) out.push("vitals");
  for (const k of Object.keys(orig.claim)) if (orig.claim[k] && orig.claim[k] !== f.claim[k]) out.push(k);
  return out;
}

/** The visit form's rules; `closing` adds what closing needs (an injury's claim-form fields, §10.4 #5). */
export function visitRules(f: Form, o: VisitOptions, closing: boolean, needsReason: boolean): Rule[] {
  const injury = f.kind === "Injury";
  return [
    ["v-complaint", !f.complaint.trim(), "Write the complaint."],
    ["v-reason", needsReason && !f.correctionReason.trim(), "Write why the recorded visit is being changed."],
    ["v-disp", closing && !f.disposition, "This visit can't close yet: it needs the disposition."],
    ["v-occ", closing && injury && !f.occurredAt, "An injury needs when it happened."],
    ["v-loc", closing && injury && !f.location.trim(), "An injury needs where it happened."],
    ["v-narr", closing && injury && !f.narrative.trim(), "An injury needs how it happened."],
    ["v-parts", closing && injury && !f.bodyParts.length, "An injury needs the body part(s)."],
    ["v-nature", closing && injury && !f.natureOfInjury.trim(), "An injury needs the nature of the injury."],
    ...o.claimQuestions.map((q): Rule => [`v-claim-${q.key}`, closing && injury && !f.claim[q.key.charAt(0).toUpperCase() + q.key.slice(1)], `An injury needs “${q.label}”.`]),
    ["v-sup", closing && injury && !f.supervisingAdult.trim(), "An injury needs the supervising adult."],
    ["v-wit", closing && injury && !f.witnesses.trim(), "An injury needs the witnesses (or “none”)."],
  ];
}

function YesNo({ id, value, onChange, problems }: { id: string; value: string; onChange: (v: string) => void; problems: Problems }) {
  return (
    <div id={id} className="flex gap-2" role="radiogroup">
      {["Y", "N"].map((x) => (
        <button key={x} type="button" role="radio" aria-checked={value === x} className={`min-h-11 min-w-16 rounded-md border px-3 text-sm ${value === x ? "border-program bg-program text-white" : problems[id] ? "border-red-500 bg-white" : "border-slate-300 bg-white"}`} onClick={() => onChange(value === x ? "" : x)}>
          {x === "Y" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

export function VisitForm({ personKey, original, options, startComplaint, onSaved, onCancel }: { personKey: string; original: VisitView | null; options: VisitOptions; startComplaint?: string; onSaved: (d: VisitDetail) => void; onCancel?: () => void }) {
  const orig = formOf(original, startComplaint);
  const [f, setF] = useState<Form>(orig);
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<Form>) => setF({ ...f, ...p });
  const changedRecorded = original ? rewritten(orig, f) : [];
  const rulesFor = (c: boolean) => visitRules(f, options, c, changedRecorded.length > 0);
  const { problems, check } = useProblems(rulesFor(closing));
  const P = ({ k }: { k: string }) => <FieldProblem id={k} problems={problems} />;
  const injury = f.kind === "Injury";
  async function save(close: boolean) {
    setClosing(close);
    setError(null);
    if (!check(rulesFor(close))) return;
    setBusy(true);
    // Times are instants on the visit's own day (a visit from yesterday keeps its date).
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(original ? Date.parse(original.startedAtUtc) : appNow()));
    const at = (k: "startedAt" | "endedAt" | "occurredAt") => (original && f[k] === orig[k] ? undefined : f[k] ? new Date(easternToUtcMs(day, f[k])).toISOString() : "");
    const times = { startedAt: at("startedAt"), endedAt: at("endedAt"), occurredAt: at("occurredAt"), vitals: { ...f.vitals, takenAt: /^\d{2}:\d{2}$/.test(f.vitals.takenAt) ? new Date(easternToUtcMs(day, f.vitals.takenAt)).toISOString() : f.vitals.takenAt } };
    const body = { ...f, ...times, ...(original ? {} : { personKey }), claim: undefined, ...Object.fromEntries(options.claimQuestions.map((q) => [q.key, f.claim[q.key.charAt(0).toUpperCase() + q.key.slice(1)] ?? ""])), close };
    const r = original ? await api<{ detail: VisitDetail }>("PUT", `/api/visits/${encodeURIComponent(original.visitKey)}`, body) : await api<{ detail: VisitDetail }>("POST", "/api/visits", body);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.detail);
  }
  const text = (k: keyof Form, id: string, label: string, opts: { area?: boolean; placeholder?: string; required?: boolean } = {}) => (
    <div>
      <label htmlFor={id} className="field-label">{label}{opts.required && <span className="text-red-600"> *</span>}</label>
      {opts.area ? (
        <textarea id={id} className="field-input min-h-20" {...invalid(id, problems)} value={String(f[k] ?? "")} placeholder={opts.placeholder} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
      ) : (
        <input id={id} className="field-input" {...invalid(id, problems)} value={String(f[k] ?? "")} placeholder={opts.placeholder} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
      )}
      <P k={id} />
    </div>
  );
  return (
    <div className="space-y-4">
      <section className="section-card space-y-3">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kind">
          {options.kinds.map((k) => (
            <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`min-h-11 rounded-md border px-4 text-sm font-medium ${f.kind === k ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => set({ kind: k })}>{KIND_LABEL[k] ?? k}</button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><label htmlFor="v-in" className="field-label">Time in</label><input id="v-in" type="time" className="field-input" value={f.startedAt} onChange={(e) => set({ startedAt: e.target.value })} /></div>
          <div><label htmlFor="v-out" className="field-label">Time out</label><input id="v-out" type="time" className="field-input" value={f.endedAt} onChange={(e) => set({ endedAt: e.target.value })} /></div>
        </div>
        {text("complaint", "v-complaint", "Complaint", { area: true, required: true })}
        <div className="grid gap-3 sm:grid-cols-2">
          {text("location", "v-loc", injury ? "Where it happened (place)" : "Place", { required: injury, placeholder: "Med station, gym, field trip site…" })}
          {text("activity", "v-act", "Activity at the time")}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.offCampus} onChange={(e) => set({ offCampus: e.target.checked })} />Off campus</label>
      </section>

      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Vitals (as taken; all optional)</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {([["temp", "Temp"], ["pulse", "Pulse"], ["bp", "BP"], ["resp", "Resp"], ["spo2", "SpO₂"]] as const).map(([k, label]) => (
            <div key={k}><label htmlFor={`v-vit-${k}`} className="field-label">{label}</label><input id={`v-vit-${k}`} className="field-input" inputMode={k === "bp" ? "text" : "decimal"} value={f.vitals[k]} onChange={(e) => set({ vitals: { ...f.vitals, [k]: e.target.value } })} /></div>
          ))}
          <div><label htmlFor="v-vit-at" className="field-label">Taken at</label><input id="v-vit-at" type="time" className="field-input" value={f.vitals.takenAt ? hhmmOf(f.vitals.takenAt) || f.vitals.takenAt : ""} onChange={(e) => set({ vitals: { ...f.vitals, takenAt: e.target.value } })} /></div>
        </div>
      </section>

      {injury && (
        <section className="section-card space-y-3 border-l-4 border-l-amber-500">
          <h3 className="text-base font-semibold">Injury: the claim form's fields</h3>
          <div><label htmlFor="v-occ" className="field-label">When it happened<span className="text-red-600"> *</span></label><input id="v-occ" type="time" className="field-input max-w-[12rem]" {...invalid("v-occ", problems)} value={f.occurredAt} onChange={(e) => set({ occurredAt: e.target.value })} /><P k="v-occ" /></div>
          <div>
            <label htmlFor="v-narr" className="field-label">How it happened<span className="text-red-600"> *</span></label>
            <p className="text-xs text-slate-600">Be specific: {options.narrativeExample}</p>
            <textarea id="v-narr" className="field-input min-h-20" {...invalid("v-narr", problems)} value={f.narrative} onChange={(e) => set({ narrative: e.target.value })} />
            <P k="v-narr" />
          </div>
          <div id="v-parts">
            <p className="field-label">Body part(s)<span className="text-red-600"> *</span></p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm sm:grid-cols-4">
              {options.bodyParts.map((b) => (
                <label key={b} className="flex items-center gap-1.5"><input type="checkbox" checked={f.bodyParts.includes(b)} onChange={() => set({ bodyParts: f.bodyParts.includes(b) ? f.bodyParts.filter((x) => x !== b) : [...f.bodyParts, b] })} />{b}</label>
              ))}
            </div>
            <input aria-label="Other body part, side" className="field-input mt-1" placeholder="Side, or another part (e.g. left)" value={f.bodyParts.filter((b) => !options.bodyParts.includes(b)).join(", ")} onChange={(e) => set({ bodyParts: [...f.bodyParts.filter((b) => options.bodyParts.includes(b)), ...e.target.value.split(",").map((x) => x.trim()).filter(Boolean)] })} />
            <P k="v-parts" />
          </div>
          <div>
            <label htmlFor="v-nature" className="field-label">Nature of the injury<span className="text-red-600"> *</span></label>
            <input id="v-nature" className="field-input" list="v-natures" {...invalid("v-nature", problems)} value={f.natureOfInjury} onChange={(e) => set({ natureOfInjury: e.target.value })} />
            <datalist id="v-natures">{options.natures.map((n) => <option key={n} value={n} />)}</datalist>
            <P k="v-nature" />
          </div>
          <div className="space-y-2">
            {options.claimQuestions.map((q) => {
              const col = q.key.charAt(0).toUpperCase() + q.key.slice(1);
              return (
                <div key={q.key} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">{q.label}<span className="text-red-600"> *</span></span>
                  <YesNo id={`v-claim-${q.key}`} value={f.claim[col] ?? ""} onChange={(x) => set({ claim: { ...f.claim, [col]: x } })} problems={problems} />
                  <div className="w-full"><P k={`v-claim-${q.key}`} /></div>
                </div>
              );
            })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text("supervisingAdult", "v-sup", "Supervising adult", { required: true })}
            {text("supervisingTitle", "v-supt", "Their title")}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm">Did the supervising adult see it happen?</span><YesNo id="v-supw" value={f.supervisorWasWitness} onChange={(x) => set({ supervisorWasWitness: x })} problems={problems} /></div>
          {text("witnesses", "v-wit", "Other witnesses (or “none”)", { required: true })}
        </section>
      )}

      <section className="section-card space-y-3">
        {text("assessment", "v-assess", "Assessment", { area: true })}
        {text("treatment", "v-treat", "Treatment given", { area: true, placeholder: "OTC given here is recorded with “Give an OTC item”." })}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="v-disp" className="field-label">Disposition</label>
            <select id="v-disp" className="field-input min-h-11" {...invalid("v-disp", problems)} value={f.disposition} onChange={(e) => set({ disposition: e.target.value })}>
              <option value="">—</option>
              {options.dispositions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
            <P k="v-disp" />
          </div>
          <div>
            <label htmlFor="v-fu" className="field-label">Follow-up due</label>
            <input id="v-fu" type="date" className="field-input" value={f.followUpDueOn} onChange={(e) => set({ followUpDueOn: e.target.value, followUpStatus: e.target.value && !f.followUpStatus ? "Open" : f.followUpStatus })} />
            {f.followUpDueOn && <label className="mt-1 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.followUpStatus === "Done"} onChange={(e) => set({ followUpStatus: e.target.checked ? "Done" : "Open" })} />Follow-up done</label>}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm">Outside care (urgent care, ER, a physician)?</span><YesNo id="v-oc" value={f.outsideCare} onChange={(x) => set({ outsideCare: x })} problems={problems} /></div>
        {f.outsideCare === "Y" && text("outsideCareProvider", "v-ocp", "Where (provider)")}
        {text("notes", "v-notes", "Notes", { area: true })}
        {changedRecorded.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm text-amber-950">You changed something already recorded: this saves a new version; the original stays in the history.</p>
            {text("correctionReason", "v-reason", "Why the recorded visit is being changed", { required: true })}
          </div>
        )}
      </section>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save(false)}>{original ? "Save" : "Start the visit"}</button>
        <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void save(true)}>{original ? "Save and close the visit" : "Save and close"}</button>
        {onCancel && <button type="button" className="btn-secondary min-h-11" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

function VisitSummary({ v }: { v: VisitView }) {
  return (
    <div className="text-sm">
      <p><span className="font-semibold">{KIND_LABEL[v.kind] ?? v.kind}</span> · in {stamp(v.startedAtUtc)}{v.endedAtUtc ? ` · out ${stamp(v.endedAtUtc)}` : ""} · {v.location || "place not given"}{v.offCampus ? " (off campus)" : ""}</p>
      <p>Complaint: {v.complaint}</p>
      {v.vitals && <p>Vitals: {[v.vitals.temp && `T ${v.vitals.temp}`, v.vitals.pulse && `P ${v.vitals.pulse}`, v.vitals.bp && `BP ${v.vitals.bp}`, v.vitals.resp && `R ${v.vitals.resp}`, v.vitals.spo2 && `SpO₂ ${v.vitals.spo2}`].filter(Boolean).join(" · ")}</p>}
      {v.assessment && <p>Assessment: {v.assessment}</p>}
      {v.treatment && <p>Treatment: {v.treatment}</p>}
      {v.disposition && <p>Disposition: {v.disposition}</p>}
      <p className="text-xs text-slate-500">Recorded by {v.recordedByName || v.recordedByEmail}{v.correctionReason ? ` · changed: ${v.correctionReason}` : ""}</p>
    </div>
  );
}

function VisitScreen({ visitKey, me }: { visitKey: string; me: Me }) {
  const [d, setD] = useState<VisitDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [csError, setCsError] = useState<string | null>(null);
  const load = (key = visitKey) => void api<{ detail: VisitDetail }>("GET", `/api/visits/${encodeURIComponent(key)}`).then((r) => (r.ok ? setD(r.data.detail) : setError(r.data.error ?? "Could not load.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), [visitKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  const v = d.visit;
  const latest = d.latestVisitKey === v.visitKey;
  const saved = (nd: VisitDetail) => {
    setD(nd);
    if (nd.visit.visitKey !== visitKey) window.history.replaceState(null, "", `/visits/${encodeURIComponent(nd.visit.visitKey)}`);
  };
  async function countersign() {
    setCsError(null);
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/countersign`, {});
    if (r.ok) setD(r.data.detail);
    else setCsError(r.data.error ?? "Not countersigned.");
  }
  return (
    <div>
      <p className="mb-3 text-sm">
        <a href="/visits" className="text-program underline">← Visits</a> · <a href={`/chart/${encodeURIComponent(d.person.personKey)}`} className="text-program underline">Chart</a> · <a href={`/otc/${encodeURIComponent(d.person.personKey)}`} className="text-program underline">Give an OTC item</a>
      </p>
      <PersonBanner p={d.person} />
      <section className="section-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold">Visit: {d.person.lastName}, {d.person.firstName}</h2>
          <span className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded px-2 py-0.5 text-xs font-semibold ${v.status === "Open" ? "bg-amber-100 text-amber-950" : "bg-slate-200 text-slate-800"}`}>{v.status}</span>
            {v.countersignRequired && <span className="rounded bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-950">unverified: to countersign</span>}
            {v.countersignedByEmail && <span className="text-xs text-slate-600">countersigned by {v.countersignedByEmail}</span>}
          </span>
        </div>
        <p className="text-sm text-slate-600">{d.person.personKey}{d.person.dateOfBirth ? ` · born ${d.person.dateOfBirth}` : ""}{d.person.personType === "Staff" ? " · staff" : ` · ${d.person.city || "Not assigned"}`}</p>
        {v.insuranceNoticeStatus === "Due" && <p className="mt-1 text-sm font-semibold text-amber-900">Insurance notice due by {v.insuranceNoticeDueOn} (an injury with outside care).</p>}
        {v.insuranceNoticeStatus === "Sent" && <p className="mt-1 text-sm text-emerald-800">Insurance notice sent.</p>}
        {!latest && <p className="mt-1 text-sm font-medium text-amber-900">An earlier version. <a className="underline" href={`/visits/${encodeURIComponent(d.latestVisitKey)}`}>Open the latest</a>.</p>}
        {d.history.length > 0 && (
          <p className="mt-1 text-sm">
            <button type="button" className="text-program underline" onClick={() => setHistoryOpen(!historyOpen)}>corrected — see history ({d.history.length})</button>
          </p>
        )}
        {historyOpen && <ol className="mt-2 list-decimal space-y-2 pl-5">{d.history.map((h) => <li key={h.visitKey}><VisitSummary v={h} /></li>)}</ol>}
        {d.canCountersign && (
          <div className="mt-2">
            <button type="button" className="btn-secondary min-h-11" onClick={() => void countersign()}>Countersign this visit</button>
            {csError && <p className="field-error" role="alert">{csError}</p>}
          </div>
        )}
      </section>
      {d.given.length > 0 && (
        <section className="section-card">
          <h3 className="mb-1 text-base font-semibold">Given during this visit</h3>
          <ul className="text-sm">{d.given.map((a) => <li key={a.adminKey}>{a.kind} · {DISPOSITION_LABEL[a.disposition] ?? a.disposition} · {a.amountGiven} · {stamp(a.givenAtUtc)} · {a.administeredByName || a.administeredByEmail}{a.indication ? ` · for ${a.indication}` : ""}</li>)}</ul>
        </section>
      )}
      {/* One block, bound to the version shown (a new version remounts it). The keys differ from the form's:
          siblings sharing a key left stale copies of the block on the page, one per save. */}
      <VisitEscalation key={`escalation-${v.visitKey}`} d={d} onSaved={saved} />
      {latest && d.options.canEdit ? (
        <VisitForm key={`form-${v.visitKey}`}personKey={d.person.personKey} original={v} options={d.options} onSaved={saved} />
      ) : (
        <section className="section-card"><VisitSummary v={v} /></section>
      )}
      {d.notifications.length > 0 && (
        <section className="section-card">
          <h3 className="mb-1 text-base font-semibold">Parent notification</h3>
          <ul className="text-sm">{d.notifications.map((n) => <li key={n.notificationKey}>{n.tier} ({n.method}) · {n.status} · {n.toName || "—"} · drafted {stamp(n.createdUtc)}</li>)}</ul>
          <p className="mt-1 text-xs text-slate-500">Released and sent from the notification queue (next stage).</p>
        </section>
      )}
      <p className="sr-only">{me.name}</p>
    </div>
  );
}

function NewVisit({ personKey, complaint }: { personKey: string; complaint: string }) {
  const [person, setPerson] = useState<PersonHeader | null>(null);
  const [options, setOptions] = useState<VisitOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ person: PersonHeader }>("GET", `/api/otc/${encodeURIComponent(personKey)}`).then((r) => (r.ok ? setPerson(r.data.person) : setError(r.data.error ?? "Could not load.")));
    void api<{ options: VisitOptions }>("GET", "/api/visits").then((r) => r.ok && setOptions(r.data.options));
  }, [personKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!person || !options) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/visits" className="text-program underline">← Visits</a></p>
      <PersonBanner p={person} />
      <h2 className="mb-2 text-xl font-semibold">New visit: {person.lastName}, {person.firstName}</h2>
      <VisitForm personKey={personKey} original={null} options={options} startComplaint={complaint} onSaved={(d) => window.location.assign(`/visits/${encodeURIComponent(d.visit.visitKey)}`)} />
    </div>
  );
}

function VisitList() {
  const [data, setData] = useState<{ today: string; visits: VisitView[]; options: VisitOptions } | null>(null);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<{ personKey: string; name: string; note: string }[]>([]);
  const seq = useRef(0);
  useEffect(() => {
    void api<{ today: string; visits: VisitView[]; options: VisitOptions }>("GET", "/api/visits").then((r) => r.ok && setData(r.data));
  }, []);
  useEffect(() => {
    const term = q.trim();
    if (!term) return setFound([]);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api<{ people: { personKey: string; lastName: string; firstName: string; city: string }[]; staff: { personKey: string; lastName: string; firstName: string }[] }>("GET", `/api/station/search?q=${encodeURIComponent(term)}`);
      if (mine !== seq.current || !r.ok) return;
      setFound([...r.data.people.map((p) => ({ personKey: p.personKey, name: `${p.lastName}, ${p.firstName}`, note: p.city || "Not assigned" })), ...(r.data.staff ?? []).map((s) => ({ personKey: s.personKey, name: `${s.lastName}, ${s.firstName}`, note: "staff" }))]);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!data) return <p className="text-sm text-slate-600">Loading…</p>;
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Visits</h2>
      <p className="mb-3 text-sm text-slate-600">Injury, illness or other, for students and staff. Today's visits and every visit still open.</p>
      {data.options.canEdit && (
        <div className="section-card">
          <label htmlFor="vs-q" className="field-label">Start a visit: find the person</label>
          <input id="vs-q" className="field-input w-full" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" placeholder="Name, date of birth or Reference Number" />
          {found.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-100">
              {found.map((p) => <li key={p.personKey} className="py-2 text-sm"><a className="font-medium text-program underline" href={`/visits/new/${encodeURIComponent(p.personKey)}`}>{p.name}</a> <span className="text-slate-500">{p.personKey} · {p.note}</span></li>)}
            </ul>
          )}
        </div>
      )}
      <div className="section-card">
        {data.visits.length === 0 ? <p className="text-sm text-slate-600">No visits today and none open.</p> : (
          <ul className="divide-y divide-slate-100">
            {data.visits.map((v) => (
              <li key={v.visitKey} className="py-2 text-sm">
                <a className="font-medium text-program underline" href={`/visits/${encodeURIComponent(v.visitKey)}`}>{v.personName}</a>
                <span className="ml-2 text-slate-600">{KIND_LABEL[v.kind] ?? v.kind} · {stamp(v.startedAtUtc)} · {v.complaint}</span>
                <span className="ml-2 inline-flex flex-wrap gap-1">
                  <span className={`rounded px-1.5 text-xs font-semibold ${v.status === "Open" ? "bg-amber-100 text-amber-950" : "bg-slate-200 text-slate-800"}`}>{v.status}</span>
                  {v.personType === "Staff" && <span className="rounded bg-sky-100 px-1.5 text-xs text-sky-900">staff</span>}
                  {v.countersignRequired && <span className="rounded bg-amber-200 px-1.5 text-xs">to countersign</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function Visits({ path, me }: { path: string; me: Me }): React.ReactElement {
  if (path.startsWith("/visits/new/")) {
    const key = decodeURIComponent(path.slice("/visits/new/".length));
    const complaint = new URLSearchParams(window.location.search).get("complaint") ?? "";
    return <NewVisit key={key} personKey={key} complaint={complaint} />;
  }
  if (path.startsWith("/visits/")) return <VisitScreen key={path} visitKey={decodeURIComponent(path.slice("/visits/".length))} me={me} />;
  return <VisitList />;
}
